<script setup lang="ts">
import CopyButton from "@/components/CopyButton.vue";
import SwitchButton from "@/components/SwitchButton.vue";
import {
  Check,
  Download,
  Internet,
  Link,
  QrCode,
  ShareAndroid,
  Xmark,
} from "@iconoir/vue";
import { QrcodeSvg } from "qrcode.vue";
import {
  computed,
  onUnmounted,
  ref,
  useTemplateRef,
  watchEffect,
  type Ref,
} from "vue";
import type { Canvass } from "./types";

const props = defineProps<{ canvass: Canvass }>();

defineEmits<{
  publish: [];
  unpublish: [];
}>();

const canvassLink = computed(
  () => `${document.location.origin}/s/${props.canvass.shortUrlId}`,
);

const qrcodeRef = useTemplateRef<typeof QrcodeSvg>("qrcode");
const qrcodeBlob: Ref<Blob | null> = ref(null);

// TODO this is ugly - try to find a better way
// create a canvas to render the image to and create a blob from
const qrcodeCanvas = document.createElement("canvas");
const qrcodeImage = new Image();
// wait for the image to load to prevent blank canvas
qrcodeImage.onload = () => {
  qrcodeCanvas.width = qrcodeImage.naturalWidth;
  qrcodeCanvas.height = qrcodeImage.naturalHeight;
  const ctx = qrcodeCanvas.getContext("2d");
  ctx?.clearRect(0, 0, qrcodeCanvas.width, qrcodeCanvas.height);
  ctx?.drawImage(qrcodeImage, 0, 0);

  qrcodeCanvas.toBlob((blob) => {
    qrcodeBlob.value = blob;
  });
};

watchEffect(async () => {
  // create image from qrcode
  qrcodeImage.src = qrcodeRef.value?.toDataURL();
});

onUnmounted(() => {
  qrcodeCanvas.remove();
});
</script>
<template>
  <button
    class="button"
    v-bind="{ command: 'show-modal', commandfor: 'publish-canvass-dialog' }"
  >
    <template v-if="canvass.public"> <ShareAndroid />Teilen </template>
    <template v-else> <Internet />Veröffentlichen </template>
  </button>
  <dialog id="publish-canvass-dialog" v-bind="{ closedby: 'any' }">
    <div class="column">
      <header class="row row-wrap-reverse">
        <h2>Aktion veröffentlichen und teilen</h2>
        <button
          class="row-self-space-auto button button--tertiary"
          v-bind="{ command: 'close', commandfor: 'publish-canvass-dialog' }"
        >
          <span class="visually-hidden">Schließen</span> <Xmark />
        </button>
      </header>
      <p class="hint">
        Du kannst diese Aktion veröffentlichen. Eine öffentliche Aktion kann via
        Link oder QR Code geteilt werden. Menschen können sich so bei
        KiezKompass als "Supporter:in" registrieren und an dieser Aktion
        teilnehmen.
      </p>
      <p class="hint">
        Du musst diese Teilnahmen bestätigen, bevor diese Nutzer:innen die Daten
        sehen können.
      </p>
      <SwitchButton
        id="switch-public"
        :value="canvass.public"
        label="Aktion veröffentlichen"
        @checked="$emit('publish')"
        @unchecked="$emit('unpublish')"
      />
      <section v-show="canvass.public" class="column canvass-sharing">
        <QrcodeSvg
          ref="qrcode"
          :value="canvassLink"
          :size="200"
          :margin="2"
          class="qrcode"
        />
        <p class="canvass-link">{{ canvassLink }}</p>
        <CopyButton :source="canvassLink">
          <Link />Einladungslink kopieren
          <template #copied><Check />Einladungslink wurde kopiert!</template>
        </CopyButton>
        <CopyButton :source="qrcodeBlob ?? ''" :disabled="qrcodeBlob === null">
          <QrCode />QR Code kopieren
          <template #copied><Check />QR Code wurde kopiert!</template>
        </CopyButton>
        <button class="button" @click="qrcodeRef?.download()">
          <Download />QR Code runterladen (SVG)
        </button>
      </section>
    </div>
  </dialog>
</template>

<style scoped>
h2 {
  font-size: var(--font-size-md);
}

dialog {
  background-color: var(--bg-color-main);
  max-inline-size: min(
    var(--inline-size-md),
    calc(100vw - 2 * var(--spacing-sm))
  );
}

.hint {
  color: var(--text-color-muted);
}

.canvass-link {
  inline-size: 100%;
  background-color: var(--bg-color-input);
  padding: var(--padding-input);
  border-radius: var(--border-radius-input);
}

.canvass-sharing {
  button {
    inline-size: 100%;
  }
}

.qrcode {
  inline-size: 100%;
  margin-inline: auto;
}
</style>
